import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import classnames from 'classnames';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/styles';
import styles from './ImageCarousel.styles';
import { Badge } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import ErrorIcon from '@material-ui/icons/Error';
import ReportIcon from '@material-ui/icons/Report';
import { getImageUri } from '../../helpers/images';
import ImageModal from './ImageModal';

const useStyles = makeStyles(styles);

const InfoTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 400,
    // fontSize: theme.typography.pxToRem(12),
    fontSize: 16,
    border: '1px solid #dadde9',
  },
}))(Tooltip);

const CarouselImage = props => {
  const {
    tooltip,
    DndItem,
    getBadgeContent = null,
    onClick = null,
    containerStyle,
    renderPlaceholder,
    containerOffset,
    onDelete,
    deleteIconPosition = 'below-image', // or 'over-image'
    height = 140,
    debug = false,
    width: widthOverride,
    vertical,
    //
    image,
    index,
    setCurrentImage,
    blobImageSizeFolder = 'large',
    isQuickvue,
  } = props;

  const classes = useStyles();

  const [refsAreReady, setRefsAreReady] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const imageRef = useRef(null);
  const imageWrapperRef = useRef(null);

  useEffect(() => {
    setRefsAreReady(true);
  }, []);

  const DEBUG = false; // index === 2;

  const key = `tile-${index}`;

  const imageUri = getImageUri(image, { blobImageSizeFolder });

  const [imageWrapperRotationStyles, imageRotationStyles] = useMemo(() => {
    if (!refsAreReady || !imageLoaded) {
      return [{}, {}];
    }

    const img = new Image();
    img.src = imageUri;

    const imageIsPortraitByDefault = img.height > img.width;

    let isLandscape =
      typeof image.rotateDegree === 'undefined'
        ? !imageIsPortraitByDefault
        : imageIsPortraitByDefault
        ? [90, 270].includes(image.rotateDegree)
        : [0, 180].includes(image.rotateDegree);

    const imageRefDimensions = {
      height: imageRef.current?.offsetHeight,
      width: imageRef.current?.offsetWidth,
    };

    const imageWrapperDimensions = {
      height: imageWrapperRef.current?.offsetHeight,
      width: imageWrapperRef.current?.offsetWidth,
    };

    const scaleRateX = isLandscape
      ? imageRefDimensions.height / imageWrapperDimensions.width
      : imageRefDimensions.width / imageWrapperDimensions.height;
    const scaleRateY = isLandscape
      ? imageRefDimensions.width / imageWrapperDimensions.height
      : imageRefDimensions.height / imageWrapperDimensions.width;

    const scaleRate = imageIsPortraitByDefault ? Math.max(scaleRateX, scaleRateY) : Math.min(scaleRateX, scaleRateY);

    const updatedImageHeight = imageIsPortraitByDefault
      ? isLandscape
        ? imageRefDimensions.height * (isLandscape ? 1 : scaleRate)
        : undefined
      : isLandscape
      ? undefined
      : imageRefDimensions.height * (isLandscape ? 1 : scaleRate);

    const updatedImageWidth = imageIsPortraitByDefault
      ? isLandscape
        ? imageRefDimensions.width * (isLandscape ? 1 : scaleRate)
        : undefined
      : isLandscape
      ? undefined
      : imageRefDimensions.width * (isLandscape ? 1 : scaleRate);

    const negativeHorizontalMargins = imageIsPortraitByDefault
      ? isLandscape
        ? -(imageRefDimensions.width - updatedImageHeight * scaleRate) / 2
        : 0
      : isLandscape
      ? 0
      : -(imageRefDimensions.width - updatedImageHeight) / 2;

    const transformString = imageIsPortraitByDefault
      ? isLandscape
        ? `rotate(${image.rotateDegree || 0}deg) scale(${scaleRate || 1})`
        : `rotate(${image.rotateDegree || 0}deg)`
      : isLandscape
      ? `rotate(${image.rotateDegree || 0}deg)`
      : `rotate(${image.rotateDegree || 0}deg) scale(${scaleRate || 1})`;

    const imageWrapperRotationStyles = {
      marginLeft: negativeHorizontalMargins,
      marginRight: negativeHorizontalMargins,
    };

    const imageRotationStyles = {
      transform: transformString,
    };

    DEBUG &&
      (() => {
        window.image = imageRef.current;
        console.log({
          imageIsPortraitByDefault,
          isLandscape,
          scaleRateX,
          scaleRateY,
          scaleRate,
          imageRefDimensions,
          imageWrapperDimensions,
          updatedImageHeight,
          updatedImageWidth,
          negativeHorizontalMargins,
          //
          transformString,
          imageWrapperRotationStyles,
          imageRotationStyles,
        });
      })();

    return [imageWrapperRotationStyles, imageRotationStyles];
  }, [image.rotateDegree, refsAreReady, imageLoaded]);

  DEBUG &&
    console.log({
      imageWrapperRotationStyles,
      imageRotationStyles,
    });

  const showDangerIcon = image.confidenceScore >= 61 && image.confidenceScore <= 100;

  let el = (
    <div
      className={classnames(classes.imageCarouselTile, vertical && classes.imageCarouselTileVertical)}
      key={key}
      onClick={() => (!!onClick ? onClick(image, index) : setCurrentImage(image))}
      ref={imageWrapperRef}
      style={imageWrapperRotationStyles}
    >
      <img
        ref={imageRef}
        className={classnames(
          classes.imageCarouselImage,
          vertical && classes.imageCarouselImageVertical,
          !!onDelete && deleteIconPosition === 'below-image' && classes.imageCarouselImageWithDeleteIcon,
        )}
        style={{
          ...imageRotationStyles,
          cursor: 'pointer',

          ...(isQuickvue && {
            width: widthOverride,
            height: 'auto',
            maxWidth: 'unset',
          }),
        }}
        key={`tile-img-${imageRotationStyles.transform}`}
        alt="Question Thumbnail"
        src={imageUri}
        onLoad={e => {
          // DEBUG && console.log('LOADED!', e.target);
          setImageLoaded(true);
        }}
      />

      <div
        className={classnames(
          classes.imageCarouselImage,
          vertical && classes.imageCarouselImageVertical,
          !!onDelete && deleteIconPosition === 'below-image' && classes.imageCarouselImageWithDeleteIcon,
          image.hideImage && classes.imageCarouselImageWithHiddenVisibility,
          image.fraudulent && classes.imageCarouselImageWithFraudulentIndicator,
        )}
        style={{
          ...imageRotationStyles,
          cursor: 'pointer',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: 1,

          // ...(isQuickvue && {
          //   maxWidth: 'unset',
          // }),
        }}
      />

      {!!onDelete &&
        React.createElement(deleteIconPosition === 'below-image' ? DeleteIcon : RemoveCircleIcon, {
          style: {
            color: 'var(--jmi-red)',
            marginTop: 5,
            cursor: 'pointer',

            ...(deleteIconPosition === 'below-image'
              ? {}
              : {
                  position: 'absolute',
                  top: -15,
                  right: -10,
                  borderRadius: '50%',
                  backgroundColor: 'white',
                }),
          },
          onClick: event => {
            event.stopPropagation();
            onDelete(image, index);
          },
        })}

      <div
          className={classnames(
            classes.hiddenImageOverlay,
            !!onDelete && deleteIconPosition === 'below-image' && classes.hiddenImageOverlayWithDeleteIcon,
          )}
        >
          <div style={{ display: 'flex', gap: '10px' }}>
            {image.hideImage && <VisibilityOffIcon />}
            {image.fraudulent && <ReportIcon />}
          </div>
        </div>
    </div>
  );

  if (getBadgeContent) {
    el = (
      <Badge badgeContent={getBadgeContent(image, index)} color="error">
        {el}
      </Badge>
    );
  }

  if (tooltip) {
    el = (
      <InfoTooltip
        key={key}
        placement="bottom"
        interactive // doesn't close if the tooltip is hovered over
        title={tooltip(image, index)}
      >
        {el}
      </InfoTooltip>
    );
  }

  if (DndItem) {
    el = (
      <DndItem key={key} itemProps={image}>
        {el}
      </DndItem>
    );
  }

  return (
    <div
      key={key + `-el-parent`}
      style={{
        marginRight: 15,
        display: 'inherit',
        position: 'relative',

        ...(isQuickvue && {
          width: widthOverride,
        }),
      }}
    >
      {el}
      {showDangerIcon && (
        <ErrorIcon
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            color: 'red',
            zIndex: 2,
          }}
        />
      )}
    </div>
  );
};

const ImageCarousel = props => {
  const {
    images,
    tooltip,
    DndItem,
    getBadgeContent = null,
    onClick = null,
    containerStyle,
    renderPlaceholder,
    containerOffset,
    onDelete,
    deleteIconPosition = 'below-image', // or 'over-image'
    height = 140,
    debug = false,
    width: widthOverride,
    vertical,
    isQuickvue = false,
  } = props;

  const classes = useStyles();
  const containerRef = useRef(null);
  const [width, setWidth] = useState(widthOverride || 800);
  const [currentImage, setCurrentImage] = useState(null);

  const checkSize = () => {
    try {
      if (containerRef.current && !widthOverride) {
        setWidth(containerRef.current.parentElement.offsetWidth - containerOffset);
      } else if (widthOverride) {
        setWidth(widthOverride);
      }
    } catch (e) {
      console.log('ImageCarousel e', e);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', checkSize);
  }, []);

  useEffect(() => {
    checkSize();
  }, [widthOverride]);

  if (!images.length && !renderPlaceholder) {
    return null;
  }

  return (
    <React.Fragment>
      <div
        ref={containerRef}
        key={'image-carousel-outer'}
        className={classnames(
          //
          classes.imageCarouselOuter,
          vertical && classes.imageCarouselOuterVertical,
        )}
        style={{
          width,
          height: vertical ? undefined : height,
          marginBottom: 15,
          ...containerStyle,
        }}
      >
        <div
          key={'image-carousel-inner'}
          id={'image-carousel-scrollview'}
          className={classnames(
            //
            classes.imageCarouselInner,
            vertical && classes.imageCarouselInnerVertical,
          )}
          style={{
            height: vertical ? undefined : height - 15,
          }}
        >
          {images && !!images.length ? (
            images.map((image, index) => (
              <CarouselImage
                //
                key={`tile-${index}`}
                {...props}
                width={isQuickvue ? width * 0.8 : null}
                isQuickvue={isQuickvue}
                image={image}
                index={index}
                setCurrentImage={setCurrentImage}
              />
            ))
          ) : (
            <div className="flex-1 row-center-center text-center text-hint" style={{ marginRight: 30 }}>
              {typeof renderPlaceholder === 'function'
                ? renderPlaceholder()
                : typeof renderPlaceholder === 'string'
                ? renderPlaceholder
                : 'No images available'}
            </div>
          )}
        </div>
      </div>

      <ImageModal image={currentImage} open={!!currentImage} onClose={() => setCurrentImage(null)} />
    </React.Fragment>
  );
};

ImageCarousel.defaultProps = {
  tooltip: null,
  DndItem: null,
  containerStyle: {},
  renderPlaceholder: false,
  containerOffset: 20,
  onDelete: null,
};

ImageCarousel.propTypes = {
  images: PropTypes.array,
  tooltip: PropTypes.func,
  DndItem: PropTypes.func, // HOC
  containerStyle: PropTypes.object,
  renderPlaceholder: PropTypes.bool,
  containerOffset: PropTypes.number,
  onDelete: PropTypes.func,
};

export default ImageCarousel;
